import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { consultationService } from '../services/consultationService'
import { userService } from '../services/userService'
import { Consultation, ConsultationStatus } from '../types/consultation'
import { formatDate } from '../utils/locale'
import './ConsultationList.css'

interface ConsultationListProps {
  patientId: string
  onConsultationClick?: (consultation: Consultation) => void
  navigateToDetail?: boolean // If true, navigate to detail page instead of calling onConsultationClick
}

export function ConsultationList({ patientId, onConsultationClick, navigateToDetail = false }: ConsultationListProps) {
  const navigate = useNavigate()
  const { data: consultations, isLoading, error } = useQuery({
    queryKey: ['consultations', patientId],
    queryFn: () => consultationService.getByPatient(patientId),
  })

  const { data: users } = useQuery({
    queryKey: ['users'],
    queryFn: () => userService.getAll(),
  })

  // Enrich consultations with staffName if missing
  const enrichedConsultations = consultations?.map((consultation) => {
    if (consultation.staffName) {
      return consultation
    }
    const staff = users?.find((u) => u.id === consultation.staffId)
    if (staff) {
      return {
        ...consultation,
        staffName: `${staff.firstName} ${staff.lastName}`,
      }
    }
    return consultation
  })

  const getStatusBadge = (status: ConsultationStatus) => {
    const statusClasses = {
      'in-progress': 'status-in-progress',
      completed: 'status-completed',
      cancelled: 'status-cancelled',
      'no-show': 'status-no-show',
    }
    return <span className={`status-badge ${statusClasses[status]}`}>{status}</span>
  }

  if (isLoading) {
    return <div className="loading">Loading consultations...</div>
  }

  if (error) {
    return <div className="error">Error loading consultations</div>
  }

  if (!enrichedConsultations || enrichedConsultations.length === 0) {
    return (
      <div className="consultation-list-empty">
        <p>No consultations found for this patient.</p>
      </div>
    )
  }

  return (
    <div className="consultation-list">
      <div className="consultation-list-header">
        <h3>Consultations ({enrichedConsultations.length})</h3>
      </div>
      <div className="consultation-items">
        {enrichedConsultations.map((consultation) => (
          <div
            key={consultation.id}
            className="consultation-item"
            onClick={() => {
              if (navigateToDetail) {
                navigate(`/consultations/${consultation.id}`)
              } else {
                onConsultationClick?.(consultation)
              }
            }}
          >
            <div className="consultation-item-header">
              <div className="consultation-date">
                <strong>{formatDate(consultation.consultationDate)}</strong>
                {consultation.startTime && (
                  <span className="consultation-time">
                    {' '}
                    {consultation.startTime}
                    {consultation.endTime && ` - ${consultation.endTime}`}
                  </span>
                )}
              </div>
              {getStatusBadge(consultation.status)}
            </div>
            <div className="consultation-item-body">
              {consultation.reason && (
                <div className="consultation-reason">
                  <strong>Reason:</strong> {consultation.reason}
                </div>
              )}
              {consultation.staffName && (
                <div className="consultation-staff">
                  <strong>Provider:</strong> {consultation.staffName}
                </div>
              )}
              {consultation.diagnosis && consultation.diagnosis.length > 0 && (
                <div className="consultation-diagnosis">
                  <strong>Diagnosis:</strong>{' '}
                  {consultation.diagnosis.map((d, i) => (
                    <span key={i} className="diagnosis-item">
                      {d}
                      {i < consultation.diagnosis!.length - 1 && ', '}
                    </span>
                  ))}
                </div>
              )}
              {consultation.notes && (
                <div className="consultation-notes">
                  {consultation.notes.length > 150
                    ? `${consultation.notes.substring(0, 150)}...`
                    : consultation.notes}
                </div>
              )}
              {consultation.artefacts && consultation.artefacts.length > 0 && (
                <div className="consultation-artefacts">
                  <strong>Artefacts:</strong> {consultation.artefacts.length}{' '}
                  {consultation.artefacts.length === 1 ? 'item' : 'items'}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

